import { createServerFn, ClientSafeError } from "catmint/server";

/**
 * A server function that returns the current server time.
 * When called from the client, this is automatically transformed
 * into a fetch() call to a server endpoint.
 */
export const getServerTime = createServerFn(async () => {
  return {
    time: new Date().toISOString(),
    pid: process.pid,
    nodeVersion: process.version,
  };
});

/**
 * A server function that greets a user by name.
 * Demonstrates passing input to a server function.
 */
export const greetUser = createServerFn(
  async (input: { name: string }) => {
    // Simulate a slow server operation
    await new Promise((r) => setTimeout(r, 500));
    return {
      message: `Hello, ${input.name}! This greeting was generated on the server (PID ${process.pid}).`,
      timestamp: new Date().toISOString(),
    };
  },
  { method: "POST" },
);

// ---------------------------------------------------------------------------
// Error handling demos
// ---------------------------------------------------------------------------

/**
 * Throws a ClientSafeError — the message and data are sent to the client.
 * On the client this arrives as a thrown ServerFnError with the same
 * statusCode and data.
 */
export const throwClientSafeError = createServerFn(async () => {
  throw new ClientSafeError("Insufficient funds", {
    statusCode: 422,
    data: { available: 50, requested: 100 },
  });
});

/**
 * Returns a ClientSafeError instead of throwing it. The client receives
 * a ServerFnError as the return value (it is NOT thrown).
 */
export const returnClientSafeError = createServerFn(async () => {
  return new ClientSafeError("Payment method expired", {
    statusCode: 402,
    data: { expiredAt: "2025-01-15" },
  });
});

/**
 * Throws a plain Error. The client never sees the real message — in dev
 * it gets "[DEV ONLY] ..." and in prod "Internal Server Error [ref: ...]".
 */
export const throwUnsafeError = createServerFn(async () => {
  // Simulates an unexpected bug — a database connection string, stack trace,
  // or other sensitive detail that should never reach the browser.
  throw new Error(
    "Connection refused: postgres://admin:s3cret@10.0.0.5:5432/prod",
  );
});
